import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminManage.css';

interface Store {
  _id: string;
  name: string;
  ref: string;
  logo: string;
  colors: {
    primary: string;
    background: string;
    buttonHover: string;
    statusBorder: string;
    timelineDot: string;
    cardBackground: string;
  };
  text: {
    title: string;
    placeholder: string;
    trackButton: string;
    trackingButton: string;
    clearButton: string;
    statusLabel: string;
    historyTitle: string;
    errorEnterNumber: string;
    errorTracking: string;
    errorConnection: string;
    pendingTitle: string;
    pendingDescription: string;
    pendingReasons: string[];
    pendingTryAgain: string;
    noResultsMessage: string;
  };
}

const defaultStore: Omit<Store, '_id'> = {
  name: '',
  ref: '',
  logo: '',
  colors: {
    primary: '#000000',
    background: '#FFFFFF',
    buttonHover: '#000000',
    statusBorder: '#333333',
    timelineDot: '#333333',
    cardBackground: '#FFFFFF',
  },
  text: {
    title: '🚚 מעקב חבילות',
    placeholder: 'הזינו מספר מעקב',
    trackButton: '🔍 עקוב',
    trackingButton: '⏳ מחפש...',
    clearButton: 'נקה',
    statusLabel: 'סטטוס:',
    historyTitle: '📋 היסטוריית מעקב',
    errorEnterNumber: 'נא להזין מספר מעקב',
    errorTracking: 'שגיאה במעקב אחר החבילה',
    errorConnection: 'שגיאה בהתחברות לשרת',
    pendingTitle: '⏳ מידע על המעקב לא זמין כרגע',
    pendingDescription: 'מספר המעקב נרשם במערכת, אך המידע עדיין לא זמין.',
    pendingReasons: [
      'המתינו 5-30 דקות עד שהמערכת תשלוף את המידע',
      'ייתכן שהחבילה חדשה מאוד ועדיין לא נסרקה',
      'ייתכן שמספר המעקב אינו תקין',
    ],
    pendingTryAgain: 'נסו לעקוב שוב בעוד מספר דקות 🔄',
    noResultsMessage: 'לא נמצא מידע על מספר המעקב. אנא בדקו את המספר ונסו שוב.',
  },
};

const AdminManage: React.FC = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Store management state
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [formData, setFormData] = useState<Omit<Store, '_id'>>(defaultStore);

  useEffect(() => {
    // Check if already authenticated in session
    const authenticated = sessionStorage.getItem('adminAuthenticated');
    if (authenticated === 'true') {
      setIsAuthenticated(true);
      fetchStores();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await axios.post('/api/admin/login', {
        username,
        password,
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuthenticated', 'true');
        fetchStores();
      } else {
        setLoginError('שם משתמש או סיסמה לא תקינים');
      }
    } catch (err: any) {
      setLoginError(err.response?.data?.message || 'ההתחברות נכשלה');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuthenticated');
    setUsername('');
    setPassword('');
  };

  const fetchStores = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/stores');
      if (response.data.success) {
        setStores(response.data.data);
      }
    } catch (err) {
      setError('שגיאה בטעינת החנויות');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAnalytics = () => {
    window.location.href = '/admin/analytics';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingStore) {
        const response = await axios.put(`/api/stores/${editingStore._id}`, formData);
        if (response.data.success) {
          setStores(stores.map(s => s._id === editingStore._id ? response.data.data : s));
          resetForm();
        }
      } else {
        const response = await axios.post('/api/stores', formData);
        if (response.data.success) {
          setStores([response.data.data, ...stores]);
          resetForm();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'שגיאה בשמירת החנות');
      console.error(err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק חנות זו?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`/api/stores/${id}`);
      if (response.data.success) {
        setStores(stores.filter(s => s._id !== id));
      }
    } catch (err) {
      setError('שגיאה במחיקת החנות');
      console.error(err);
    }
  };

  const handleEdit = (store: Store) => {
    setEditingStore(store);
    setFormData({
      name: store.name,
      ref: store.ref,
      logo: store.logo,
      colors: { ...store.colors },
      text: { ...store.text },
    });
    setShowForm(true);
  };

  const handleAddNew = () => {
    if (showForm && !editingStore) {
      // If form is already showing for a new store, just close it
      resetForm();
    } else {
      // Reset form data and editing state first with deep copy
      setEditingStore(null);
      setFormData({
        ...defaultStore,
        colors: { ...defaultStore.colors },
        text: { 
          ...defaultStore.text,
          pendingReasons: [...defaultStore.text.pendingReasons]
        }
      });
      setError('');
      // Then show the form
      setShowForm(true);
    }
  };

  const resetForm = () => {
    setFormData({
      ...defaultStore,
      colors: { ...defaultStore.colors },
      text: { 
        ...defaultStore.text,
        pendingReasons: [...defaultStore.text.pendingReasons]
      }
    });
    setEditingStore(null);
    setShowForm(false);
    setError('');
  };

  const handleInputChange = (field: string, value: any) => {
    // Ensure color values are properly formatted with # prefix
    if (field.startsWith('colors.') && typeof value === 'string') {
      // Remove any existing # and whitespace
      let colorValue = value.trim().replace(/^#+/, '');
      // Add # prefix if it's a valid hex color
      if (colorValue.length > 0 && /^[0-9A-Fa-f]{3,8}$/.test(colorValue)) {
        value = '#' + colorValue;
      } else if (colorValue.length === 0) {
        value = '#000000';
      }
    }

    setFormData(prev => {
      const keys = field.split('.');
      if (keys.length === 1) {
        return { ...prev, [field]: value };
      } else if (keys.length === 2) {
        return {
          ...prev,
          [keys[0]]: {
            ...(prev as any)[keys[0]],
            [keys[1]]: value,
          },
        };
      }
      return prev;
    });
  };

  const handleArrayChange = (index: number, value: string) => {
    const newReasons = [...formData.text.pendingReasons];
    newReasons[index] = value;
    setFormData(prev => ({
      ...prev,
      text: {
        ...prev.text,
        pendingReasons: newReasons,
      },
    }));
  };

  // Login Form
  if (!isAuthenticated) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-box">
            <h1>🔐 התחברות מנהל</h1>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>שם משתמש:</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                />
              </div>
              <div className="form-group">
                <label>סיסמה:</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              {loginError && <div className="error-message">{loginError}</div>}
              <button type="submit" className="btn-login">
                התחבר
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main Admin Panel
  if (loading) {
    return <div className="admin-container"><h2>טוען...</h2></div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>ניהול חנויות</h1>
        <div className="header-actions">
          <button className="btn-analytics" onClick={handleViewAnalytics}>
            📊 אנליטיקס
          </button>
          <button className="btn-add" onClick={handleAddNew}>
            {showForm ? 'ביטול' : '+ הוסף חנות חדשה'}
          </button>
          <button className="btn-logout" onClick={handleLogout}>
            התנתק
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="store-form">
          <h2>{editingStore ? 'ערוך חנות' : 'הוסף חנות חדשה'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>מידע בסיסי</h3>
              <div className="form-group">
                <label>שם החנות:</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>מזהה (מפתח URL):</label>
                <input
                  type="text"
                  value={formData.ref}
                  onChange={e => handleInputChange('ref', e.target.value)}
                  required
                  placeholder="לדוגמה: mystore, store1"
                />
              </div>
              <div className="form-group">
                <label>כתובת לוגו:</label>
                <input
                  type="text"
                  value={formData.logo}
                  onChange={e => handleInputChange('logo', e.target.value)}
                  required
                  placeholder="לדוגמה: /store-logo.png"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>צבעים (קודי Hex)</h3>
              <div className="color-grid">
                <div className="color-input-group">
                  <label>צבע ראשי:</label>
                  <div className="color-picker-wrapper">
                    <input
                      type="color"
                      value={formData.colors.primary.startsWith('#') ? formData.colors.primary : '#' + formData.colors.primary}
                      onChange={e => handleInputChange('colors.primary', e.target.value)}
                    />
                    <input
                      type="text"
                      value={formData.colors.primary}
                      onChange={e => handleInputChange('colors.primary', e.target.value)}
                      placeholder="#000000"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="color-input-group">
                  <label>צבע רקע:</label>
                  <div className="color-picker-wrapper">
                    <input
                      type="color"
                      value={formData.colors.background.startsWith('#') ? formData.colors.background : '#' + formData.colors.background}
                      onChange={e => handleInputChange('colors.background', e.target.value)}
                    />
                    <input
                      type="text"
                      value={formData.colors.background}
                      onChange={e => handleInputChange('colors.background', e.target.value)}
                      placeholder="#FFFFFF"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="color-input-group">
                  <label>רקע כרטיסייה:</label>
                  <div className="color-picker-wrapper">
                    <input
                      type="color"
                      value={formData.colors.cardBackground.startsWith('#') ? formData.colors.cardBackground : '#' + formData.colors.cardBackground}
                      onChange={e => handleInputChange('colors.cardBackground', e.target.value)}
                    />
                    <input
                      type="text"
                      value={formData.colors.cardBackground}
                      onChange={e => handleInputChange('colors.cardBackground', e.target.value)}
                      placeholder="#FFFFFF"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="color-input-group">
                  <label>צבע ריחוף כפתור:</label>
                  <div className="color-picker-wrapper">
                    <input
                      type="color"
                      value={formData.colors.buttonHover.startsWith('#') ? formData.colors.buttonHover : '#' + formData.colors.buttonHover}
                      onChange={e => handleInputChange('colors.buttonHover', e.target.value)}
                    />
                    <input
                      type="text"
                      value={formData.colors.buttonHover}
                      onChange={e => handleInputChange('colors.buttonHover', e.target.value)}
                      placeholder="#000000"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="color-input-group">
                  <label>מסגרת סטטוס:</label>
                  <div className="color-picker-wrapper">
                    <input
                      type="color"
                      value={formData.colors.statusBorder.startsWith('#') ? formData.colors.statusBorder : '#' + formData.colors.statusBorder}
                      onChange={e => handleInputChange('colors.statusBorder', e.target.value)}
                    />
                    <input
                      type="text"
                      value={formData.colors.statusBorder}
                      onChange={e => handleInputChange('colors.statusBorder', e.target.value)}
                      placeholder="#333333"
                      maxLength={7}
                    />
                  </div>
                </div>

                <div className="color-input-group">
                  <label>נקודת ציר זמן:</label>
                  <div className="color-picker-wrapper">
                    <input
                      type="color"
                      value={formData.colors.timelineDot.startsWith('#') ? formData.colors.timelineDot : '#' + formData.colors.timelineDot}
                      onChange={e => handleInputChange('colors.timelineDot', e.target.value)}
                    />
                    <input
                      type="text"
                      value={formData.colors.timelineDot}
                      onChange={e => handleInputChange('colors.timelineDot', e.target.value)}
                      placeholder="#333333"
                      maxLength={7}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>תוכן טקסט</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>כותרת:</label>
                  <input
                    type="text"
                    value={formData.text.title}
                    onChange={e => handleInputChange('text.title', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>טקסט חסר:</label>
                  <input
                    type="text"
                    value={formData.text.placeholder}
                    onChange={e => handleInputChange('text.placeholder', e.target.value)}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>כפתור מעקב:</label>
                  <input
                    type="text"
                    value={formData.text.trackButton}
                    onChange={e => handleInputChange('text.trackButton', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>כפתור מחפש:</label>
                  <input
                    type="text"
                    value={formData.text.trackingButton}
                    onChange={e => handleInputChange('text.trackingButton', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>סיבות המתנה (3 פריטים):</label>
                {formData.text.pendingReasons.map((reason, index) => (
                  <input
                    key={index}
                    type="text"
                    value={reason}
                    onChange={e => handleArrayChange(index, e.target.value)}
                    placeholder={`סיבה ${index + 1}`}
                    style={{ marginBottom: '8px' }}
                  />
                ))}
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save">
                {editingStore ? 'עדכן חנות' : 'צור חנות'}
              </button>
              <button type="button" className="btn-cancel" onClick={resetForm}>
                ביטול
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="stores-list">
        <h2>כל החנויות</h2>
        {stores.length === 0 ? (
          <p>לא נמצאו חנויות. הוסף את החנות הראשונה שלך!</p>
        ) : (
          <div className="stores-grid">
            {stores.map(store => (
              <div key={store._id} className="store-card">
                <div className="store-logo">
                  <img src={store.logo} alt={store.name} onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/150';
                  }} />
                </div>
                <h3>{store.name}</h3>
                <p className="store-ref">מזהה: {store.ref}</p>
                <div className="store-preview" style={{ 
                  backgroundColor: store.colors.background,
                  color: store.colors.primary,
                  border: `2px solid ${store.colors.primary}`,
                }}>
                  <p>{store.text.title}</p>
                </div>
                <div className="color-swatches">
                  <div className="swatch" style={{ backgroundColor: store.colors.primary }} title="ראשי" />
                  <div className="swatch" style={{ backgroundColor: store.colors.background, border: '1px solid #ddd' }} title="רקע" />
                  <div className="swatch" style={{ backgroundColor: store.colors.buttonHover }} title="ריחוף כפתור" />
                  <div className="swatch" style={{ backgroundColor: store.colors.timelineDot }} title="נקודת ציר" />
                </div>
                <div className="store-actions">
                  <button className="btn-edit" onClick={() => handleEdit(store)}>
                    ערוך
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(store._id)}>
                    מחק
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManage;
